
import { useState } from "react";
import { supabase } from "@/integrations/supabase/client";
import { useToast } from "@/hooks/use-toast";

export type SupportedLanguage = {
  code: string;
  name: string;
  flag?: string;
};

export const supportedLanguages: SupportedLanguage[] = [
  { code: "en", name: "English", flag: "🇬🇧" },
  { code: "da", name: "Dansk", flag: "🇩🇰" },
  { code: "de", name: "Deutsch", flag: "🇩🇪" },
  { code: "fr", name: "Français", flag: "🇫🇷" },
  { code: "es", name: "Español", flag: "🇪🇸" },
  { code: "it", name: "Italiano", flag: "🇮🇹" },
  { code: "nl", name: "Nederlands", flag: "🇳🇱" },
  { code: "pl", name: "Polski", flag: "🇵🇱" },
  { code: "sv", name: "Svenska", flag: "🇸🇪" },
];

export function useTranslation() {
  const [isTranslating, setIsTranslating] = useState<boolean>(false);
  const { toast } = useToast();

  const translateText = async (
    text: string | string[],
    targetLang: string,
    sourceLang?: string
  ) => {
    if (!text || (Array.isArray(text) && text.length === 0)) {
      console.log("Nothing to translate, returning empty result");
      return { translations: [] };
    }

    if (!targetLang) {
      console.error("No target language provided");
      toast({
        title: "Translation error",
        description: "No target language specified.",
        variant: "destructive",
      });
      return { translations: [] };
    }

    // Process and send the texts for translation
    console.log(`Starting translation to ${targetLang}`, {
      textCount: Array.isArray(text) ? text.length : 1
    });
    
    setIsTranslating(true);

    try {
      const { data, error } = await supabase.functions.invoke("translate-text", {
        body: { 
          text, 
          targetLang, 
          sourceLang 
        },
      });

      if (error) {
        console.error("Translation error:", error);
        toast({
          title: "Translation failed",
          description: "Could not translate the text. Please try again later.",
          variant: "destructive",
        });
        return { translations: [] };
      }

      console.log("Translation response:", {
        hasTranslations: !!data?.translations,
        translationsCount: data?.translations?.length || 0
      });
      
      if (!data || !Array.isArray(data.translations)) {
        console.error("Invalid response format:", data);
        toast({
          title: "Translation error",
          description: "Received invalid response format.",
          variant: "destructive",
        });
        return { translations: [] };
      }

      return data;
    } catch (err) {
      console.error("Translation error:", err);
      toast({
        title: "Translation error",
        description: "An unexpected error occurred.",
        variant: "destructive",
      });
      return { translations: [] };
    } finally {
      setIsTranslating(false);
    }
  };

  const translateBatched = async (
    texts: string[],
    targetLang: string,
    batchSize: number = 20,
    onProgress?: (progress: number, total: number) => void
  ) => {
    if (!texts || texts.length === 0) {
      return { translations: [] };
    }

    console.log(`Translating ${texts.length} texts in batches of ${batchSize}`);
    setIsTranslating(true);
    
    try {
      // Split texts into batches
      const batches = [];
      for (let i = 0; i < texts.length; i += batchSize) {
        batches.push(texts.slice(i, i + batchSize));
      }
      
      console.log(`Split translations into ${batches.length} batches`);
      
      const allTranslations: any[] = [];
      
      for (let batchIndex = 0; batchIndex < batches.length; batchIndex++) {
        const batch = batches[batchIndex];
        console.log(`Processing batch ${batchIndex + 1}/${batches.length} with ${batch.length} items`);
        
        const result = await translateText(batch, targetLang);
        
        if (result.translations && result.translations.length > 0) {
          allTranslations.push(...result.translations);
          
          if (onProgress) {
            const processed = (batchIndex + 1) * batch.length;
            onProgress(Math.min(processed, texts.length), texts.length);
          }
        } else {
          console.error(`No translations received in batch ${batchIndex + 1}`);
        }
        
        // Add a small delay between batches to prevent rate limiting
        if (batchIndex < batches.length - 1) {
          await new Promise(resolve => setTimeout(resolve, 300));
        }
      }
      
      return { translations: allTranslations };
    } catch (error) {
      console.error("Error in batch translation:", error);
      toast({
        title: "Translation failed",
        description: "Failed to translate all content. Please try again.",
        variant: "destructive",
      });
      return { translations: [] };
    } finally {
      setIsTranslating(false);
    }
  };

  return {
    translateText,
    translateBatched,
    isTranslating,
    supportedLanguages
  };
}
